import { DEFAULT_RAG_DIMENSIONS } from "apps/project-iq/components/WorkspaceOverview/WorkspaceOverview.constants";
import {
  IWorkspaceGroup,
  IWorkspaceLogs,
  IWorkspaceRagSummary,
  IWorkspaceRowData,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.types";
import { IPortfolio } from "apps/project-iq/models/IPortfolio";
import { IPractice } from "apps/project-iq/models/IPractice";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { IRagDimension } from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";

type RagDimensionKey = keyof IWorkspaceRagSummary;

const RAG_DIMENSION_KEYS: RagDimensionKey[] = [
  "overall",
  "budget",
  "schedule",
  "scope",
];

const resolveRagDimension = (
  dimensionId: RagDimensionKey,
  storedDimensions: IRagDimension[],
): IRagDimension =>
  storedDimensions.find((dimension) => dimension.id === dimensionId) ??
  DEFAULT_RAG_DIMENSIONS.find((dimension) => dimension.id === dimensionId) ??
  DEFAULT_RAG_DIMENSIONS[0];

export const extractWorkspaceLogs = (
  workspace: IProjectIQWorkspace,
): IWorkspaceLogs => ({
  milestones: workspace.projectPlan?.milestones ?? [],
  deliverables: workspace.deliverables ?? [],
  raidEntries: workspace.projectPlan?.raidEntries ?? [],
});

export const buildRagSummary = (
  workspace: IProjectIQWorkspace,
): IWorkspaceRagSummary => {
  const storedDimensions = workspace.projectPlan?.ragDimensions ?? [];

  return RAG_DIMENSION_KEYS.reduce((summary, dimensionKey) => {
    summary[dimensionKey] = resolveRagDimension(dimensionKey, storedDimensions);
    return summary;
  }, {} as IWorkspaceRagSummary);
};

export const toWorkspaceRowData = (
  workspace: IProjectIQWorkspace,
): IWorkspaceRowData => ({
  workspace,
  ragSummary: buildRagSummary(workspace),
  logs: extractWorkspaceLogs(workspace),
});

export const groupWorkspacesByPortfolio = (
  portfolios: IPortfolio[],
  workspaces: IProjectIQWorkspace[],
): IWorkspaceGroup[] =>
  [...portfolios]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((portfolio) => ({
      group: portfolio,
      workspaceRows: workspaces
        .filter((workspace) => workspace.portfolioId === portfolio.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(toWorkspaceRowData),
    }));

export const groupWorkspacesByPractice = (
  practices: IPractice[],
  workspaces: IProjectIQWorkspace[],
): IWorkspaceGroup[] =>
  [...practices]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((practice) => ({
      group: practice,
      workspaceRows: workspaces
        .filter((workspace) => workspace.practiceIds?.includes(practice.id) ?? false)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(toWorkspaceRowData),
    }));
